import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  createBaseSelector,
  registerSlice,
  StatusType
} from '@shared/lib/redux'

import { addressRepository } from '../domain'
import { AddressModel } from '../domain/address.model'
import {
  AddressListDTO,
  AddressListResponseDTO,
  CityListByRegionType,
  CityListDTO
} from '../types'

export interface AddressStore {
  currentCity: AddressModel | null
  currentAddress: AddressModel | null
  cityList: CityListByRegionType
  cityPage: number
  cityTotal: number
  citySize: number
  addressList: AddressModel[]
  addressPage: number
  addressSize: number
  addressTotal: number
  status: StatusType
  error: string | null
}

const initialState: AddressStore = {
  currentCity: addressRepository.getCity(),
  currentAddress: null,
  cityList: {},
  cityPage: 1,
  cityTotal: 0,
  citySize: 0,
  addressList: [],
  addressPage: 1,
  addressSize: 0,
  addressTotal: 0,
  status: 'idle',
  error: null
}

const addressSlice = createSlice({
  name: 'addressSlice',
  initialState,
  reducers: {
    getCityListStart(state, _action: PayloadAction<CityListDTO>) {
      const isInitialLoad = state.cityTotal === 0
      state.status = isInitialLoad ? 'loading' : 'fetching'
    },
    getCityListSuccess(state, action: PayloadAction<AddressListResponseDTO>) {
      state.cityPage = action.payload.meta.page
      state.citySize = action.payload.meta.size
      state.cityTotal = action.payload.meta.total

      state.cityList = {}
      state.cityList = action.payload.items.reduce(
        (acc, city) => {
          const country = city.getCountry()
          acc[country] = acc[country] || []
          acc[country].push(city)
          return acc
        },
        {} as Record<string, AddressModel[]>
      )

      state.status = 'success'
      state.error = null
    },
    getCityListFailure(state, action: PayloadAction<string>) {
      state.error = action.payload
      state.status = 'error'
    },
    getAddressList(state, _action: PayloadAction<AddressListDTO>) {
      state.status = 'loading'
      state.addressPage = 1
    },
    getAddressListSuccess(
      state,
      action: PayloadAction<AddressListResponseDTO>
    ) {
      state.addressList = action.payload.items
      state.status = 'success'
    },
    getAddressListFailure(state, action: PayloadAction<string>) {
      state.error = action.payload
      state.status = 'error'
    },
    loadMoreAddresses(state, _action: PayloadAction<AddressListDTO>) {
      state.status = 'fetching'
    },
    loadMoreAddressesSuccess(
      state,
      action: PayloadAction<AddressListResponseDTO>
    ) {
      state.addressList.push(...action.payload.items)
      state.status = 'success'
    },
    loadMoreAddressesFailed(state, action: PayloadAction<string>) {
      state.error = action.payload
      state.status = 'error'
    },
    setCurrentCity(state, action: PayloadAction<AddressModel>) {
      state.currentCity = action.payload
    },
    setCurrentAddress(state, action: PayloadAction<AddressModel>) {
      state.currentAddress = action.payload
    }
  }
})

const addressBaseSelector = createBaseSelector(addressSlice)
registerSlice([addressSlice])

const selectCurrentCity = createSelector(addressBaseSelector, (s) => {
  return s.currentCity
})

const selectCurrentAddress = createSelector(addressBaseSelector, (s) => {
  return s.currentAddress
})

const selectCityList = createSelector(addressBaseSelector, (s) => {
  return {
    cities: s.cityList,
    status: s.status,
    error: s.error
  }
})

const selectAddressList = createSelector(addressBaseSelector, (s) => {
  return {
    addresses: s.addressList,
    status: s.status,
    error: s.error
  }
})

const selectStatus = createSelector(addressBaseSelector, (s) => {
  return {
    status: s.status,
    error: s.error
  }
})

const selectCanLoadCityMore = createSelector(addressBaseSelector, (s) => {
  const lastPage = s.cityPage
  const pageSize = s.citySize
  const total = s.cityTotal
  return lastPage * pageSize < total
})

const selectCanLoadAddressesMore = createSelector(addressBaseSelector, (s) => {
  const lastPage = s.addressPage
  const pageSize = s.addressSize
  const total = s.addressTotal
  return lastPage * pageSize < total
})

const selectLastCityPage = createSelector(addressBaseSelector, (s) => {
  return s.cityPage
})
const selectLastAddressPage = createSelector(addressBaseSelector, (s) => {
  return s.addressPage
})

export const addressStore = {
  actions: { ...addressSlice.actions },
  selectors: {
    selectCurrentCity,
    selectLastCityPage,
    selectCityList,
    selectCanLoadCityMore,
    selectCurrentAddress,
    selectAddressList,
    selectCanLoadAddressesMore,
    selectLastAddressPage,
    selectStatus
  },
  name: addressSlice.name
}
